import React, { useState, useCallback, memo } from 'react';
import {
    TextField, Button, Box, Typography,
    InputAdornment, IconButton, Alert,
    Paper, useTheme, useMediaQuery, alpha
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
    LockOutlined,
    Visibility,
    VisibilityOff,
    EmailOutlined,
    PersonAddAlt1
} from '@mui/icons-material';
import api from '../utils/Api';

// 🎯 Memoized Form Components
const AuthTextField = memo(({
    label,
    name,
    type = 'text',
    value,
    onChange,
    onKeyPress,
    showPassword,
    onTogglePassword,
    size,
    theme,
    isMobile,
    startIcon: StartIcon,
    error,
    helperText
}) => (
    <TextField
        fullWidth
        margin="normal"
        variant="outlined"
        label={label}
        name={name}
        type={type}
        value={value}
        onChange={onChange}
        onKeyPress={onKeyPress}
        size={size}
        error={error}
        helperText={helperText}
        InputProps={{
            startAdornment: StartIcon && (
                <InputAdornment position="start">
                    <StartIcon sx={{
                        color: error ? 'error.main' : 'primary.main',
                        fontSize: isMobile ? '1.2rem' : '1.5rem'
                    }} />
                </InputAdornment>
            ),
            endAdornment: onTogglePassword && (
                <InputAdornment position="end">
                    <IconButton
                        onClick={onTogglePassword}
                        edge="end"
                        size={size}
                        sx={{
                            color: 'text.secondary',
                            '&:hover': {
                                color: 'primary.main',
                                bgcolor: alpha(theme.palette.primary.main, 0.1)
                            }
                        }}
                    >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                </InputAdornment>
            )
        }}
        sx={{
            '& .MuiOutlinedInput-root': {
                borderRadius: isMobile ? 2 : 3,
                fontSize: isMobile ? '0.9rem' : '1rem',
                transition: 'all 0.2s ease-in-out',
                '&:hover fieldset': {
                    borderColor: error ? 'error.main' : 'primary.main',
                    borderWidth: '2px'
                },
                '&.Mui-focused fieldset': {
                    borderColor: error ? 'error.main' : 'primary.main',
                    borderWidth: '2px',
                    boxShadow: error
                        ? '0 0 0 4px rgba(244, 67, 54, 0.1)'
                        : '0 0 0 4px rgba(102, 126, 234, 0.1)'
                },
                '& fieldset': {
                    borderWidth: '2px',
                    borderColor: error
                        ? alpha(theme.palette.error.main, 0.3)
                        : alpha(theme.palette.primary.main, 0.2)
                }
            },
            '& .MuiInputLabel-root': {
                fontWeight: 600,
                fontSize: isMobile ? '0.9rem' : '1rem'
            },
            '& .MuiFormHelperText-root': {
                fontSize: isMobile ? '0.75rem' : '0.875rem'
            }
        }}
    />
));

const SuperAdminRegisterPage = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const navigate = useNavigate();

    const [form, setForm] = useState({
        name: '',
        email: '',
        password: '',
        confirmPassword: ''
    });

    const [errors, setErrors] = useState({});
    const [apiError, setApiError] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const validateForm = useCallback(() => {
        const newErrors = {};
        if (!form.name.trim()) newErrors.name = 'Name is required';
        if (!form.email) newErrors.email = 'Email is required';
        else if (!/\S+@\S+\.\S+/.test(form.email)) newErrors.email = 'Invalid email format';
        if (!form.password) newErrors.password = 'Password is required';
        else if (form.password.length < 8) newErrors.password = 'Password must be at least 8 characters';
        if (form.password !== form.confirmPassword) newErrors.confirmPassword = 'Passwords do not match';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }, [form]);

    const handleChange = useCallback((e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
        if (apiError) setApiError('');
    }, [errors, apiError]);

    const handleSubmit = useCallback(async () => {
        if (!validateForm()) return;

        try {
            setIsLoading(true);
            setApiError('');

            await api.post('/auth/register', {
                name: form.name,
                email: form.email,
                password: form.password,
                role: 'superadmin', // Force superadmin role
                schoolName: 'Super Admin HQ',
                schoolType: 'Private'
            });

            setApiError('✅ Account Created! Redirecting to login...');
            setTimeout(() => navigate('/super-admin/login'), 2000);

        } catch (err) {
            setApiError(err.response?.data?.message || 'Registration failed.');
        } finally {
            setIsLoading(false);
        }
    }, [form, navigate, validateForm]);

    return (
        <Box sx={{
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #1a237e 0%, #311b92 100%)', // Distinct Deep Purple/Blue
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 2
        }}>
            <Paper elevation={24} sx={{
                maxWidth: 500,
                width: '100%',
                borderRadius: 4,
                overflow: 'hidden',
                position: 'relative',
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(30, 41, 59, 0.96)' : 'rgba(255, 255, 255, 0.96)'
            }}>
                <Box sx={{ p: 4, textAlign: 'center' }}>
                    <Box sx={{
                        display: 'inline-flex',
                        p: 2,
                        borderRadius: '50%',
                        bgcolor: 'secondary.main',
                        color: 'white',
                        mb: 2,
                        boxShadow: '0 8px 25px rgba(0,0,0,0.2)'
                    }}>
                        <PersonAddAlt1 fontSize="large" />
                    </Box>

                    <Typography variant="h4" fontWeight="bold" gutterBottom color="secondary.main">
                        New Super Admin
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Create a new master control account
                    </Typography>

                    {apiError && (
                        <Alert severity={apiError.startsWith('✅') ? "success" : "error"} sx={{ mb: 3, borderRadius: 2 }}>
                            {apiError}
                        </Alert>
                    )}

                    <AuthTextField
                        label="Full Name"
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        theme={theme}
                        isMobile={isMobile}
                        error={!!errors.name}
                        helperText={errors.name}
                    />

                    <AuthTextField
                        label="Email Address"
                        name="email"
                        type="email"
                        value={form.email}
                        onChange={handleChange}
                        theme={theme}
                        isMobile={isMobile}
                        startIcon={EmailOutlined}
                        error={!!errors.email}
                        helperText={errors.email}
                    />

                    <AuthTextField
                        label="Password"
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        value={form.password}
                        onChange={handleChange}
                        theme={theme}
                        isMobile={isMobile}
                        startIcon={LockOutlined}
                        showPassword={showPassword}
                        onTogglePassword={() => setShowPassword(!showPassword)}
                        error={!!errors.password}
                        helperText={errors.password}
                    />

                    <AuthTextField
                        label="Confirm Password"
                        name="confirmPassword"
                        type={showPassword ? 'text' : 'password'}
                        value={form.confirmPassword}
                        onChange={handleChange}
                        theme={theme}
                        isMobile={isMobile}
                        startIcon={LockOutlined}
                        error={!!errors.confirmPassword}
                        helperText={errors.confirmPassword}
                    />

                    <Button
                        fullWidth
                        variant="contained"
                        color="secondary"
                        size="large"
                        onClick={handleSubmit}
                        disabled={isLoading}
                        sx={{
                            mt: 2,
                            py: 1.5,
                            borderRadius: 3,
                            fontSize: '1.1rem',
                            fontWeight: 'bold',
                            textTransform: 'none',
                            boxShadow: '0 8px 25px rgba(49, 27, 146, 0.4)'
                        }}
                    >
                        {isLoading ? 'Creating...' : 'Register'}
                    </Button>

                    <Button
                        fullWidth
                        variant="text"
                        sx={{ mt: 2, textTransform: 'none' }}
                        onClick={() => navigate('/super-admin/login')}
                    >
                        Back to Login
                    </Button>
                </Box>
            </Paper>
        </Box>
    );
};

export default SuperAdminRegisterPage;
